/* ─────────────────────────────────────────────────────────────────────────────
   Pipeline Funnel (All) — page-local UI kit: the shared GrowthOS DataTable
   (growthos-shared/GxPagedTable) in this page's look, the reference app's
   TaperedFunnel (sqrt-scaled trapezoid bars) and its ScopeTag / StatusPill /
   HubSpot link primitives.
   ──────────────────────────────────────────────────────────────────────────── */
import React from 'react'
import { Box, Paper, Tooltip, Typography } from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DateRangeIcon from '@mui/icons-material/DateRange'
import {
  GX_BORDER, GX_INK_500, GX_INK_700, GX_INK_900, GX_COLORS, GX_CARD_SX, GX_NEGATIVE,
} from '../growthos-shared/GrowthOsUi'
import { GxExpandableTable, GxPagedTable } from '../growthos-shared/GxPagedTable'
import { integer, percentage } from '../growthos-shared/gxFormat'

export { integer, percentage, compactCurrency } from '../growthos-shared/gxFormat'

export const GX_INK_400 = '#94a3b8'
export const GX_INK_300 = '#cbd5e1'

export const MBR_TITLE = 'Est. MBR = estimated monthly business revenue (₹). This is the cross-pipeline value currency: shipment potential is a D2C-only field (populated for ~65% of D2C deals but under 1% elsewhere), so it cannot compare pipelines.'

export const dealLink = (hub, id) => (hub ? `https://${hub.uiDomain}/contacts/${hub.portalId}/record/0-3/${id}` : undefined)

// ── small primitives ────────────────────────────────────────────────────────
export function ScopeTag({ label, title }) {
  const pill = (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.5, borderRadius: '9999px',
      bgcolor: `${GX_COLORS.indigo}10`, color: GX_COLORS.indigo, border: `1px solid ${GX_COLORS.indigo}26`,
      fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', cursor: title ? 'help' : 'default',
    }}>
      <DateRangeIcon sx={{ fontSize: 12 }} /> {label}
    </Box>
  )
  return title ? <Tooltip title={title} arrow>{pill}</Tooltip> : pill
}

export function Badge({ children, title, sx }) {
  const el = (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: '9999px',
      border: `1px solid ${GX_BORDER}`, bgcolor: '#fff', color: GX_INK_700, fontSize: 11, fontWeight: 600,
      whiteSpace: 'nowrap', cursor: title ? 'help' : 'default', ...sx,
    }}>{children}</Box>
  )
  return title ? <Tooltip title={title} arrow>{el}</Tooltip> : el
}

const STATUS_TINT = {
  Open: { bg: 'rgba(79,70,229,0.12)', fg: '#4338ca' },
  Won: { bg: 'rgba(5,150,105,0.12)', fg: '#047857' },
  Lost: { bg: 'rgba(225,29,72,0.12)', fg: '#be123c' },
}
export function StatusPill({ status }) {
  const t = STATUS_TINT[status] || STATUS_TINT.Open
  return <Badge sx={{ bgcolor: t.bg, color: t.fg, borderColor: 'transparent' }}>{status}</Badge>
}

export function HubspotLink({ hub, dealId, label = 'Deal' }) {
  const href = dealLink(hub, dealId)
  if (!href) return <Box component="span" sx={{ color: GX_INK_300 }}>—</Box>
  return (
    <Box component="a" href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#4338ca', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
      {label} <OpenInNewIcon sx={{ fontSize: 12 }} />
    </Box>
  )
}

export const Muted = ({ children = '—' }) => <Box component="span" sx={{ color: GX_INK_300 }}>{children}</Box>
export const Neg = ({ children }) => <Box component="span" sx={{ color: GX_NEGATIVE }}>{children}</Box>

/** Section card matching the reference `.card p-5`: title + description on the left, tags on the right. */
export function PfSection({ title, icon, description, right, children }) {
  return (
    <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 2.5, mb: 2.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 18, fontWeight: 700, color: GX_INK_900 }}>
            {icon}{title}
          </Typography>
          {description && <Typography sx={{ fontSize: 13, color: GX_INK_500, mt: 0.25 }}>{description}</Typography>}
        </Box>
        {right && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>{right}</Box>}
      </Box>
      {children}
    </Paper>
  )
}

// The shared GrowthOS table in this page's look (ExpandableTable owns its single-row expansion).
export const DataTable = (props) => <GxPagedTable variant="pipeline" {...props} />
export const ExpandableTable = (props) => <GxExpandableTable variant="pipeline" {...props} />

// ── TaperedFunnel (reference components/TaperedFunnel.tsx) ──────────────────
export function funnelWidthPct(pct, floor = 24) {
  const p = Math.max(0, Math.min(100, pct == null ? 0 : pct)) / 100
  return floor + (100 - floor) * Math.sqrt(p)
}

export function TaperedFunnel({ stages, onStageClick, minWidthPct, activeKey }) {
  return (
    <Box sx={{ mx: 'auto', maxWidth: 672, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      {stages.map((s) => {
        const Icon = s.icon
        const active = activeKey != null && activeKey === s.key
        return (
          <Box key={s.key} component={onStageClick ? 'button' : 'div'} onClick={onStageClick ? () => onStageClick(s.key) : undefined}
            sx={{
              width: `${funnelWidthPct(s.pct, minWidthPct)}%`, p: 0, border: 'none', bgcolor: 'transparent', textAlign: 'left',
              cursor: onStageClick ? 'pointer' : 'default', transition: 'transform 200ms',
              '&:hover': onStageClick ? { transform: 'scale(1.02)' } : undefined,
              filter: active ? 'drop-shadow(0 0 6px rgba(79,70,229,0.55))' : undefined,
            }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, color: '#fff', background: s.gradient,
              clipPath: 'polygon(4% 0%, 96% 0%, 100% 100%, 0% 100%)',
              boxShadow: 'inset 0 2px 0 rgba(255,255,255,0.4), inset 0 -4px 8px rgba(0,0,0,0.22)',
              textShadow: '0 1px 2px rgba(0,0,0,0.25)',
            }}>
              {Icon && <Icon sx={{ fontSize: 17, opacity: 0.9, flexShrink: 0 }} />}
              <Box sx={{ display: 'flex', minWidth: 0, flex: 1, alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5 }}>
                <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>{s.label}</Typography>
                <Typography sx={{ flexShrink: 0, fontSize: 16, fontWeight: 700 }}>
                  {integer(s.count)}
                  {s.pct != null && <Box component="span" sx={{ ml: 0.5, fontSize: 12, fontWeight: 500, opacity: 0.8 }}>({percentage(s.pct)})</Box>}
                </Typography>
              </Box>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}
